// Seasonal Earth colour: NASA's Blue Marble Next Generation, one cloud-free
// month of 2004 each (snow cover and vegetation; flat ocean with true-colour
// shallows; no sea ice). June is a blend of May and July, which NASA no
// longer serves. Each month is taken as its middle, and the globe blends
// the two months either side of the selected moment.

const pad = (month) => String(month + 1).padStart(2, "0");

export const seasonPath = (month, res) => `/seasons/earth-${pad(month)}-${res}.jpg`;

// Months (0-11) either side of `date` and how far it is from the first to
// the second.
export function monthBlend(date) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const intoMonth = (date.getUTCDate() - 1 + date.getUTCHours() / 24) / days;
  const position = month + intoMonth - 0.5;
  const first = Math.floor(position);
  return { lower: (first + 12) % 12, upper: (first + 13) % 12, t: position - first };
}

// Loads month textures on demand, keeping the few most recently used (a 4k
// month is ~45 MB on the GPU). load(path) resolves to a texture; prepare
// runs once on it and release when it's evicted.
export function createSeasonTextures({ load, prepare = () => {}, release = () => {}, keep = 4 }) {
  const cache = new Map(); // key -> Promise<Texture>, oldest first
  const get = (month, res) => {
    const key = `${month}-${res}`;
    let entry = cache.get(key);
    if (entry) {
      cache.delete(key);
    } else {
      entry = load(seasonPath(month, res)).then((texture) => {
        prepare(texture);
        return texture;
      });
      entry.catch(() => cache.delete(key));
    }
    cache.set(key, entry);
    while (cache.size > keep) {
      const [oldKey, oldEntry] = cache.entries().next().value;
      cache.delete(oldKey);
      oldEntry.then(release, () => {});
    }
    return entry;
  };
  const has = (month, res) => cache.has(`${month}-${res}`);
  // Seed with a texture loaded elsewhere (the first view's).
  const put = (month, res, texture) => {
    prepare(texture);
    cache.set(`${month}-${res}`, Promise.resolve(texture));
  };
  return { get, has, put };
}
